import { useMemo } from "react";

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  BoxProps,
  Center,
  Flex,
  Icon,
  Tag,
  Text,
} from "@chakra-ui/react";
import { GoArrowUpRight } from "react-icons/go";

import BaseImage from "@components/BaseImage";
import UTMLink from "@components/UTMLink";
import {
  LEFTNAV_DOCS,
  LEFTNAV_OPTION_CLICK,
  LEFTNAV_SOCIAL_MEDIA,
} from "@constants/analytics";
import {
  COMMUNITY_DETAILS,
  getCommunityIcon,
  SidebarNavs,
  SidebarTag,
} from "@constants/common";
import useBanner from "@hooks/useBanner";
import useDappConfig from "@hooks/useDappConfig";
import useLedgerLive from "@hooks/useLedgerLive";
import { usePathname } from "next/navigation";
import { GoDotFill } from "react-icons/go";
import { trackEvent } from "utils";

interface SidebarProps extends BoxProps {}

const commonTagStyles = {
  fontWeight: "600",
  fontSize: "10px",
  p: "0px 2px",
  borderRadius: "4px",
  lineHeight: "12px",
  letterSpacing: "0.4px",
  minH: "16px",
};

export const getTagBasedOnType = (tag: SidebarTag) => {
  switch (tag) {
    case SidebarTag.COMING_SOON:
      return (
        <Tag
          bg={{ base: "backgroundCanvas", xl: "backgroundCard" }}
          color="textPrimary"
          {...commonTagStyles}
        >
          COMING SOON
        </Tag>
      );
    case SidebarTag.NEW:
      return (
        <Tag bg="#FD6480" color="white" {...commonTagStyles}>
          New
        </Tag>
      );
    case SidebarTag.HOT:
      return (
        <Tag bg="#FD6480" color="white" {...commonTagStyles} opacity={"0.6"}>
          HOT
        </Tag>
      );
    case SidebarTag.INDICATOR:
      return (
        <Tag
          bg="transparent"
          color="#FD6480"
          {...commonTagStyles}
          p="0"
          pr="6px"
          minW="0"
        >
          <GoDotFill />
        </Tag>
      );
  }
};

const Sidebar = ({ ...rest }: SidebarProps) => {
  const pathname = usePathname();

  const { swapEnabledChains } = useDappConfig();

  const { isLedgerConnected } = useLedgerLive();

  const { showBanner } = useBanner();

  const filteredOptions = useMemo(() => {
    let options = SidebarNavs;
    if (isLedgerConnected) {
      options = options
        .filter((item) => !item.disableForLedger)
        .map((item) => {
          if (item.items) {
            item.items = item.items.filter((item) => !item.disableForLedger);
          }
          return item;
        });
    }
    if (!swapEnabledChains.length) {
      options = options.map((item) => {
        return {
          ...item,
          items: item.items?.filter((opt) => opt.key !== "wrap"),
        };
      });
    }
    return options;
  }, [isLedgerConnected, swapEnabledChains]);

  const defaultOpenMenuIndex = useMemo(() => {
    let defaultIndex = 0;
    let itemsCount = 0;
    filteredOptions.forEach((option) => {
      if (option.items) {
        option.items.forEach((item) => {
          if (item.link + "/" === pathname) {
            defaultIndex = itemsCount;
          }
        });
        itemsCount++;
      }
    });
    return defaultIndex;
  }, [filteredOptions, pathname]);

  return (
    <Flex
      height="100vh"
      flexDir="column"
      justifyContent="space-between"
      gap={{ base: "40px", xl: "64px" }}
      p={{ base: "32px 0 0 0", xl: "32px 12px 32px 40px" }}
      mt={showBanner ? "1rem" : "0"}
      position={{ lg: "sticky" }}
      top="0"
      borderRight={{ xl: "1px solid #C9E7E7" }}
      {...rest}
    >
      <Flex
        h={"calc(100vh - 48px)"}
        pos="relative"
        flexDir="column"
        justifyContent="space-between"
      >
        <Box
          overflowY="auto"
          overflowX="hidden"
          maxH={"94%"}
          className="scroll-container"
        >
          <UTMLink href="/" isExternal={isLedgerConnected ? true : false}>
            <Center h="26px" justifyContent="flex-start" cursor="pointer">
              <BaseImage
                src="/assets/kelp.svg"
                alt="kelp"
                height={26}
                width={70}
                loading="eager"
              />
            </Center>
          </UTMLink>

          <Accordion
            mt="40px"
            allowMultiple
            defaultIndex={[defaultOpenMenuIndex]}
          >
            {filteredOptions.map((option) => {
              if (option.items) {
                return (
                  <AccordionItem
                    key={option.key}
                    borderTop="1px solid"
                    borderColor="#C9E7E7"
                  >
                    <AccordionButton
                      p="0"
                      width="100%"
                      justifyContent="left"
                      _hover={{
                        bg: "inherit",
                      }}
                      _active={{
                        bg: "inherit",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {option.title ? (
                        <Text
                          color="textSecondary"
                          fontWeight="600"
                          fontSize="12px"
                          lineHeight="24px"
                          p={3}
                          pr="6px"
                          textTransform="uppercase"
                        >
                          {option.title}
                        </Text>
                      ) : option?.icon?.src ? (
                        <Box p={3} pr="6px">
                          <BaseImage
                            src={option.icon.src}
                            alt={option.key}
                            height={option.icon.height}
                            width={option.icon.width}
                          />
                        </Box>
                      ) : null}
                      {option.tag && getTagBasedOnType(option.tag)}
                      {/* <AccordionIcon /> */}
                      <Box mr={4} ml="auto">
                        <AccordionIcon />
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4} pt={0} px={0}>
                      <Box>
                        {option.items.map((data) => (
                          <UTMLink
                            href={data.link ?? "#"}
                            isExternal={data.isExternal || false}
                            onClick={() =>
                              trackEvent(LEFTNAV_OPTION_CLICK, {
                                cta: data.title,
                                cta_location: data.link,
                                redirect_link: data.link,
                              })
                            }
                            key={data.key}
                          >
                            <Flex
                              borderRadius={"8px"}
                              gap="6px"
                              p="12px 18px"
                              w="full"
                              alignItems="center"
                              bg={
                                pathname === `${data.link}/`
                                  ? "#B6DFDF"
                                  : "transparent"
                              }
                              _hover={{
                                bg: "#E4F8F8",
                              }}
                            >
                              <BaseImage
                                src={
                                  pathname === `${data.link}/`
                                    ? data.activeIcon
                                    : data.icon
                                }
                                alt={data.key}
                                height={24}
                                width={24}
                              />
                              <Text
                                color="textSecondary"
                                fontWeight="500"
                                fontSize="14px"
                                lineHeight="16px"
                                _hover={{
                                  textDecoration: "none",
                                  color: "#075A5A",
                                }}
                                gap={2}
                                whiteSpace="nowrap"
                              >
                                {data.title}
                              </Text>
                              {data.tag && getTagBasedOnType(data.tag)}
                              {data.isExternal && (
                                <Icon mr="-16px" as={GoArrowUpRight} mt="2px" />
                              )}
                            </Flex>
                          </UTMLink>
                        ))}
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                );
              }

              return (
                <UTMLink
                  href={option.link ?? "#"}
                  // isExternal={data.isExternal}
                  key={option.key}
                >
                  <Flex
                    gap="12px"
                    borderRadius={"8px"}
                    p={3}
                    pr={4}
                    borderTop="1px solid"
                    borderColor="#C9E7E7"
                    justifyContent="space-between"
                    alignItems="center"
                    bg={
                      pathname === `${option.link}/` ? "#B6DFDF" : "transparent"
                    }
                  >
                    <Text
                      fontWeight="600"
                      fontSize="12px"
                      lineHeight="18px"
                      color="textSecondary"
                      textTransform="uppercase"
                    >
                      {option.title}
                    </Text>
                    {option.rightIcon && (
                      <BaseImage
                        src={option.rightIcon}
                        alt={option.key}
                        height={16}
                        width={16}
                      />
                    )}
                  </Flex>
                </UTMLink>
              );
            })}
          </Accordion>
        </Box>

        <Flex gap={3} alignItems="center" ml="18px">
          {COMMUNITY_DETAILS.map((item) => (
            <UTMLink
              isExternal
              href={item.link}
              key={item.key}
              onClick={() => {
                trackEvent(
                  item.key === "docs" ? LEFTNAV_DOCS : LEFTNAV_SOCIAL_MEDIA,
                  {
                    redirect_link: item.link,
                    cta_location: "sidebar",
                    cta: item.name,
                  }
                );
              }}
            >
              <BaseImage
                src={getCommunityIcon(item.key) || ""}
                alt={item.key}
                height={24}
                width={24}
              />
            </UTMLink>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Sidebar;
